import { useEffect, useRef, useState } from 'react';
import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { RequireAuth } from './auth/RequireAuth';
import ChangePasswordModal from './components/ChangePasswordModal';
import Footer from './components/Footer';
import Login from './pages/Login';
import SetNewPassword from './pages/SetNewPassword';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import TransactionMonitoring from './pages/TransactionMonitoring';
import ExhaustedList from './pages/ExhaustedList';
import TransactionLookup from './pages/TransactionLookup';
import TransactionPending from './pages/TransactionPending';
import Merchants from './pages/Merchants';
import ErrorCodes from './pages/ErrorCodes';
import AccessLogs from './pages/AccessLogs';
import InstapayAppLogs from './pages/InstapayAppLogs';
import InfraMonitoring from './pages/InfraMonitoring';
import { INSTAPAY_APP_LOG_SOURCES } from './data/instapayAppLogSources';

function Shell({ children }) {
  const { email, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);

  function handleSignOut() {
    signOut();
    navigate('/login', { replace: true });
  }

  // The dropdown toggle below is a plain <a>, not a NavLink, so it never
  // gets react-router's automatic .active class the way every other nav
  // item does - computed manually here instead. Covers both /access-logs
  // and any of the 5 /instapay-app-logs/* sub-pages (2026-08-19 - "Access
  // logs" and "InstapayApp Logs" merged into one "Logs" dropdown, was two
  // separate top-level nav items before).
  const isLogsActive = location.pathname.startsWith('/access-logs')
    || location.pathname.startsWith('/instapay-app-logs');

  // Plain React-controlled dropdown (2026-08-07), not Bootstrap's JS
  // Dropdown component - same reasoning ChangePasswordModal.jsx/
  // PendingStatusModal.jsx already used to avoid Bootstrap's imperative JS
  // elsewhere in this dashboard: coordinating it with React/router state is
  // more friction than it's worth. A first attempt relied on
  // `data-bs-toggle="dropdown"` plus looking up the auto-created instance
  // via `window.bootstrap.Dropdown.getInstance(...)` to close it after a
  // NavLink click - that didn't reliably work (confirmed still broken after
  // deploying), most likely because `window.bootstrap` isn't actually the
  // same module instance Vite's bundler wires up `data-bs-toggle`'s
  // auto-init through, so `getInstance()` found nothing to close. Fully
  // self-contained instead: `dropdownOpen` state drives the `show` class
  // directly, closes automatically on every route change (covers clicking
  // any item, not just these 5), and closes on an outside click via a
  // `mousedown` listener scoped to `dropdownRef`.
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!dropdownOpen) {
      return undefined;
    }
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  return (
    <div className="app-shell">
      <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom sticky-top">
        <div className="container-fluid px-3 px-lg-4">
          <NavLink to="/" className="navbar-brand d-flex align-items-center gap-2 py-1">
            <img src="/bizmo-logo.png" alt="Bizmoto" height="30" />
          </NavLink>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#dashboardNav"
            aria-controls="dashboardNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon" />
          </button>

          <div className="collapse navbar-collapse" id="dashboardNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              <li className="nav-item">
                <NavLink to="/" end className="nav-link">
                  Progress
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/transaction-monitoring" className="nav-link">
                  Transaction Monitoring
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/exhausted" className="nav-link">
                  Exhausted
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/transaction-pending" className="nav-link">
                  Pending
                </NavLink>
              </li>              
              <li className="nav-item">
                <NavLink to="/transaction-lookup" className="nav-link">
                  Lookup
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/merchants" className="nav-link">
                  Merchant List
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/error-codes" className="nav-link">
                  Error codes
                </NavLink>
              </li>
              <li className="nav-item dropdown" ref={dropdownRef}>
                <a
                  className={`nav-link dropdown-toggle${isLogsActive ? ' active' : ''}`}
                  href="#"
                  id="logsDropdown"
                  role="button"
                  aria-expanded={dropdownOpen}
                  onClick={(e) => {
                    e.preventDefault();
                    setDropdownOpen((open) => !open);
                  }}
                >
                  Logs
                </a>
                <ul
                  className={`dropdown-menu${dropdownOpen ? ' show' : ''}`}
                  aria-labelledby="logsDropdown"
                >
                  <li>
                    <NavLink to="/access-logs" className="dropdown-item">
                      Access logs
                    </NavLink>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  {INSTAPAY_APP_LOG_SOURCES.map((s) => (
                    <li key={s.slug}>
                      <NavLink to={`/instapay-app-logs/${s.slug}`} className="dropdown-item">
                        {s.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
              <li className="nav-item">
                <NavLink to="/infra-monitoring" className="nav-link">
                  Infra Monitoring
                </NavLink>
              </li>
            </ul>
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted small">{email}</span>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowChangePassword(true)}
              >
                Change password
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={handleSignOut}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>
      <main className="app-main container-fluid px-3 px-lg-4 py-4">{children}</main>

      <Footer />

      <ChangePasswordModal show={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/set-new-password" element={<SetNewPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <Shell>
                <Dashboard />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/transaction-monitoring"
          element={
            <RequireAuth>
              <Shell>
                <TransactionMonitoring />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/exhausted"
          element={
            <RequireAuth>
              <Shell>
                <ExhaustedList />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/transaction-lookup"
          element={
            <RequireAuth>
              <Shell>
                <TransactionLookup />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/transaction-pending"
          element={
            <RequireAuth>
              <Shell>
                <TransactionPending />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/merchants"
          element={
            <RequireAuth>
              <Shell>
                <Merchants />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/error-codes"
          element={
            <RequireAuth>
              <Shell>
                <ErrorCodes />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/access-logs"
          element={
            <RequireAuth>
              <Shell>
                <AccessLogs />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/instapay-app-logs/:slug"
          element={
            <RequireAuth>
              <Shell>
                <InstapayAppLogs />
              </Shell>
            </RequireAuth>
          }
        />
        <Route
          path="/infra-monitoring"
          element={
            <RequireAuth>
              <Shell>
                <InfraMonitoring />
              </Shell>
            </RequireAuth>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
